import React from 'react';
import { Phone, AlertTriangle, CheckCircle, FileText, Shield, Scale, Users } from 'lucide-react';
export function CdlTestingInfoPage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-2138">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-2139">
        <div className="container mx-auto px-4" data-id="element-2140">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-2141">
            Michigan Commercial Driver License (CDL)
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-2142">
            Federal Requirements, Testing & Disqualifications
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-2143">
        <div className="max-w-4xl mx-auto" data-id="element-2144">
          {/* Background */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2145">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2146">
              <FileText className="mr-3 text-[#B1050B]" size={28} data-id="element-2147" />
              Commercial Motor Vehicle Safety Act of 1986
            </h2>

            <p className="text-gray-700 mb-4" data-id="element-2148">
              The Commercial Motor Vehicle Safety Act of 1986 was signed into
              law on October 27, 1986. The goal of the Act is to improve highway
              safety by ensuring that drivers of large trucks and buses are
              qualified to operate those vehicles and to remove unsafe and
              unqualified drivers from the highways.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-4" data-id="element-2149">
              <p className="text-gray-800" data-id="element-2150">
                The Act retained the State's right to issue a driver's license,
                but established <strong data-id="element-2151">minimum national standards</strong>{' '}
                which States must meet when licensing CMV drivers.
              </p>
            </div>

            <p className="text-gray-700" data-id="element-2152">
              It is important to note that the Act does not require drivers to
              obtain a separate Federal license; it merely requires States to
              upgrade their existing testing and licensing programs to conform
              with Federal minimum standards.
            </p>
          </div>

          {/* CDL Exceptions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2153">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2154">
              CDL Exceptions
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-2155">
              Michigan offers exceptions to the CDL requirements, which include:
            </p>

            <div className="space-y-4" data-id="element-2156">
              <div className="bg-green-50 border-l-4 border-green-500 p-4" data-id="element-2157">
                <p className="text-gray-800" data-id="element-2158">
                  • Active military duty operating military vehicles
                </p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4" data-id="element-2159">
                <p className="text-gray-800" data-id="element-2160">
                  • Police and firefighters operating authorized emergency
                  vehicles
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4" data-id="element-2161">
                <p className="text-gray-800" data-id="element-2162">
                  • Farmers operating within a 150-mile radius of a farm (an "F"
                  endorsement may be required)
                </p>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-500 p-4" data-id="element-2163">
                <p className="text-gray-800" data-id="element-2164">
                  • Individuals operating motor homes or vehicles used
                  exclusively to transport personal possessions or family
                  members for nonbusiness purposes
                </p>
              </div>
            </div>
          </div>

          {/* CDL Groups */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2165">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2166">
              <Scale className="mr-3 text-[#B1050B]" size={28} data-id="element-2167" />
              What Type of CDL Do I Need?
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-2168">
              A Michigan resident needs a valid operator or chauffeur license
              with the appropriate group designation to operate the following
              vehicles:
            </p>

            <div className="space-y-6" data-id="element-2169">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-2170">
                <h3 className="text-2xl font-bold text-blue-600 mb-3" data-id="element-2171">
                  Group A
                </h3>
                <p className="text-gray-700" data-id="element-2172">
                  • A combination of vehicles with a gross combination weight
                  rating of <strong data-id="element-2173">26,001 pounds or more</strong>, including a
                  towed vehicle with a gross vehicle weight rating of more than{' '}
                  <strong data-id="element-2174">10,000 pounds</strong>
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-6" data-id="element-2175">
                <h3 className="text-2xl font-bold text-green-600 mb-3" data-id="element-2176">
                  Group B
                </h3>
                <p className="text-gray-700" data-id="element-2177">
                  • A single vehicle having a gross vehicle weight rating of{' '}
                  <strong data-id="element-2178">26,001 pounds or more</strong>
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-2179">
                <h3 className="text-2xl font-bold text-yellow-600 mb-3" data-id="element-2180">
                  Group C
                </h3>
                <p className="text-gray-700 mb-2" data-id="element-2181">To operate small vehicles:</p>
                <ul className="space-y-1 text-gray-700 ml-4" data-id="element-2182">
                  <li data-id="element-2183">
                    • Designed to carry <strong data-id="element-2184">16 or more people</strong>{' '}
                    including the driver; or
                  </li>
                  <li data-id="element-2185">
                    • Not meeting the requirements for group A or B which carry{' '}
                    <strong data-id="element-2186">hazardous materials</strong> in amounts requiring
                    placarding
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Endorsements */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2187">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2188">
              What Endorsements Do I Need?
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-2189">
              In addition to the appropriate CDL group designation, endorsements
              are required for the following:
            </p>

            <div className="space-y-4" data-id="element-2190">
              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-2191">
                <span className="bg-blue-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2192">
                  T
                </span>
                <div data-id="element-2193">
                  <p className="font-bold text-gray-900" data-id="element-2194">
                    DOUBLE or TRIPLE TRAILERS
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2195">
                    (Triple-trailer combinations are not permitted in Michigan)
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-2196">
                <span className="bg-green-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2197">
                  P
                </span>
                <div data-id="element-2198">
                  <p className="font-bold text-gray-900" data-id="element-2199">PASSENGER</p>
                  <p className="text-sm text-gray-600" data-id="element-2200">
                    For vehicles designed to carry more than 15 people
                    (including the driver)
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-2201">
                <span className="bg-purple-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2202">
                  N
                </span>
                <div data-id="element-2203">
                  <p className="font-bold text-gray-900" data-id="element-2204">TANK VEHICLES</p>
                  <p className="text-sm text-gray-600" data-id="element-2205">
                    For vehicles designed to haul liquids/liquefied gases in
                    permanently mounted tanks of any size or portable tanks
                    rated at 1,000 gallons or more
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-2206">
                <span className="bg-red-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2207">
                  H
                </span>
                <div data-id="element-2208">
                  <p className="font-bold text-gray-900" data-id="element-2209">HAZARDOUS MATERIALS</p>
                  <p className="text-sm text-gray-600" data-id="element-2210">
                    To carry hazardous materials in amounts requiring placarding
                    (includes small trucks, pickup trucks and passenger
                    vehicles)
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-2211">
                <span className="bg-orange-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2212">
                  X
                </span>
                <div data-id="element-2213">
                  <p className="font-bold text-gray-900" data-id="element-2214">
                    Combined TANK and HAZARDOUS MATERIALS
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2215">
                    An "X" endorsement appears instead of H and N codes when
                    applicant receives both endorsements
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Driver Qualification File */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2216">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2217">
              Driver Qualification File
            </h2>

            <p className="text-gray-700 mb-4" data-id="element-2218">
              A driver qualification file must contain, but is not limited to:
            </p>

            <div className="space-y-2" data-id="element-2219">
              <div className="flex items-start" data-id="element-2220">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2221" />
                <span className="text-gray-700" data-id="element-2222">
                  A driver's application for employment
                </span>
              </div>
              <div className="flex items-start" data-id="element-2223">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2224" />
                <span className="text-gray-700" data-id="element-2225">
                  A copy of the driver's motor vehicle report
                </span>
              </div>
              <div className="flex items-start" data-id="element-2226">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2227" />
                <span className="text-gray-700" data-id="element-2228">
                  The driver's report of violations (annual)
                </span>
              </div>
              <div className="flex items-start" data-id="element-2229">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2230" />
                <span className="text-gray-700" data-id="element-2231">
                  Employer's review of driving record (annual)
                </span>
              </div>
              <div className="flex items-start" data-id="element-2232">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2233" />
                <span className="text-gray-700" data-id="element-2234">
                  A valid Medical Examiner's Certificate and/or waiver
                </span>
              </div>
              <div className="flex items-start" data-id="element-2235">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2236" />
                <span className="text-gray-700" data-id="element-2237">
                  Proof of a road test or equivalent (Michigan requires a copy
                  of valid driver's license for intrastate drivers)
                </span>
              </div>
            </div>
          </div>

          {/* Drunk Driving Laws */}
          <div className="bg-red-50 border-2 border-red-500 rounded-lg shadow-md p-8 mb-8" data-id="element-2238">
            <h2 className="text-2xl font-bold text-red-900 mb-6 flex items-center" data-id="element-2239">
              <AlertTriangle className="mr-3" size={28} data-id="element-2240" />
              Michigan's Drunk Driving Laws Affect CDL License
            </h2>

            <div className="space-y-6" data-id="element-2241">
              <div className="bg-white rounded-lg p-6" data-id="element-2242">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2243">BAC Standards:</h3>
                <p className="text-gray-700 mb-2" data-id="element-2244">
                  FHWA has established <strong data-id="element-2245">0.04%</strong> as the blood
                  alcohol concentration (BAC) level at or above which a CMV
                  driver is deemed to be driving under the influence of alcohol.
                </p>
                <p className="text-sm text-gray-600" data-id="element-2246">
                  States maintain a BAC level between .08% and .10% for non-CMV
                  drivers.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6" data-id="element-2247">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2248">
                  Revocation Periods:
                </h3>
                <ul className="space-y-3 text-gray-700" data-id="element-2249">
                  <li className="flex items-start" data-id="element-2250">
                    <span className="font-bold mr-2 flex-shrink-0" data-id="element-2251">
                      1 year:
                    </span>
                    <span data-id="element-2252">
                      First violation (3 years if transporting hazardous
                      material)
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2253">
                    <span className="font-bold mr-2 flex-shrink-0" data-id="element-2254">Life:</span>
                    <span data-id="element-2255">
                      Second violation, regardless of time between violations
                      (possibility of reinstatement after 10 years)
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 rounded-lg p-6" data-id="element-2256">
                <p className="text-gray-800 mb-3" data-id="element-2257">
                  <strong data-id="element-2258">Important:</strong> These revocations apply to a CDL
                  driver while operating ANY motor vehicle.
                </p>
                <p className="text-sm text-gray-700" data-id="element-2259">
                  When operating a noncommercial motor vehicle, the alcohol
                  content of .08 BAC would be used to determine a drunk driving
                  violation.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6" data-id="element-2260">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2261">
                  Other violations resulting in revocation:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-2262">
                  <li data-id="element-2263">• Leaving the scene of an accident</li>
                  <li data-id="element-2264">• Using a vehicle to commit a felony</li>
                  <li data-id="element-2265">• Refusing a request for a chemical test</li>
                </ul>
              </div>

              <p className="text-sm text-gray-600 italic" data-id="element-2266">
                These laws are contained in Act 300 of the Michigan Vehicle
                Code, Sections 257.319b and 257.625. Amendments extending these
                revocations to noncommercial vehicles became effective October
                1, 2005.
              </p>
            </div>
          </div>

          {/* Knowledge & Skills Tests */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2267">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2268">
              Knowledge & Skills Tests
            </h2>

            <div className="space-y-6" data-id="element-2269">
              <div className="bg-blue-50 rounded-lg p-6" data-id="element-2270">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2271">
                  Knowledge Tests:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-2272">
                  <li data-id="element-2273">
                    • The general knowledge test must contain at least{' '}
                    <strong data-id="element-2274">30 questions</strong>
                  </li>
                  <li data-id="element-2275">
                    • To pass, applicants must correctly answer at least{' '}
                    <strong data-id="element-2276">80 percent</strong> of the questions
                  </li>
                  <li data-id="element-2277">
                    • States develop their own tests which must be at least as
                    stringent as Federal standards
                  </li>
                </ul>
              </div>

              <div className="bg-green-50 rounded-lg p-6" data-id="element-2278">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2279">Skills Tests:</h3>
                <ul className="space-y-2 text-gray-700" data-id="element-2280">
                  <li data-id="element-2281">
                    • Applicants must successfully perform all required skills
                    (listed in 49 CFR 383.113)
                  </li>
                  <li data-id="element-2282">
                    • Skills test must be taken in a vehicle representative of
                    the type the applicant operates or expects to operate
                  </li>
                </ul>
              </div>

              <div className="bg-purple-50 rounded-lg p-6" data-id="element-2283">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2284">
                  Third Party Skills Testing:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-2285">
                  <li data-id="element-2286">
                    • States, employers, training facilities, and private
                    institutions can serve as third party skills testers
                  </li>
                  <li data-id="element-2287">• Tests must be the same as those given by the State</li>
                  <li data-id="element-2288">
                    • Examiners must meet the same qualifications as State
                    examiners
                  </li>
                  <li data-id="element-2289">
                    • States must conduct on-site inspection at least once a
                    year
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Disqualifications */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-2290">
            <h2 className="text-3xl font-bold mb-6 flex items-center" data-id="element-2291">
              <Shield className="mr-3" size={32} data-id="element-2292" />
              Disqualifications
            </h2>

            <p className="text-lg mb-6" data-id="element-2293">
              For conviction while driving a CMV, drivers must be disqualified
              and lose their privilege to drive:
            </p>

            <div className="space-y-4" data-id="element-2294">
              <div className="bg-white/10 rounded-lg p-6" data-id="element-2295">
                <p className="text-2xl font-bold mb-2" data-id="element-2296">60 to 120 days</p>
                <p className="text-gray-200" data-id="element-2297">
                  Two or more serious traffic violations within a 3-year period
                  (excessive speeding, reckless driving, improper lane changes,
                  following too closely, traffic offenses in fatal accidents)
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-2298">
                <p className="text-2xl font-bold mb-2" data-id="element-2299">90 days to 5 years</p>
                <p className="text-gray-200" data-id="element-2300">
                  One or more violations of an out-of-service order within a
                  10-year period
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-2301">
                <p className="text-2xl font-bold mb-2" data-id="element-2302">1 Year</p>
                <ul className="text-gray-200 space-y-1" data-id="element-2303">
                  <li data-id="element-2304">
                    • Driving under the influence of controlled substance or
                    alcohol
                  </li>
                  <li data-id="element-2305">• Leaving the scene of an accident</li>
                  <li data-id="element-2306">• Using a CMV to commit a felony</li>
                </ul>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-2307">
                <p className="text-2xl font-bold mb-2" data-id="element-2308">3 Years</p>
                <p className="text-gray-200" data-id="element-2309">
                  Any of the 1-year offenses while operating a CMV that is
                  placarded for hazardous materials
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-2310">
                <p className="text-2xl font-bold mb-2" data-id="element-2311">Life</p>
                <ul className="text-gray-200 space-y-1" data-id="element-2312">
                  <li data-id="element-2313">
                    • Second offense of any of the 1-year or 3-year offenses
                  </li>
                  <li data-id="element-2314">
                    • Using a CMV to commit a felony involving manufacturing,
                    distributing, or dispensing controlled substances
                  </li>
                </ul>
                <p className="text-sm text-gray-300 mt-2" data-id="element-2315">
                  States have option to reduce certain lifetime
                  disqualifications to minimum of 10 years if driver completes
                  approved rehabilitation program
                </p>
              </div>
            </div>

            <div className="bg-yellow-50 text-gray-900 rounded-lg p-6 mt-6" data-id="element-2316">
              <p className="font-semibold mb-2" data-id="element-2317">Important Notes:</p>
              <ul className="space-y-2 text-sm" data-id="element-2318">
                <li data-id="element-2319">
                  • If disqualified from operating a CMV, State may issue
                  license to operate non-CMVs
                </li>
                <li data-id="element-2320">
                  • Drivers disqualified from CMV cannot be issued "conditional"
                  or "hardship" CDL
                </li>
                <li data-id="element-2321">
                  • Out-of-state violations treated same as home State
                  convictions
                </li>
              </ul>
            </div>
          </div>

          {/* Employer Notifications */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2322">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2323">
              Employer Notifications
            </h2>

            <div className="space-y-4" data-id="element-2324">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-2325">
                <p className="text-gray-800 mb-2" data-id="element-2326">
                  <strong data-id="element-2327">Within 30 days of a conviction:</strong>
                </p>
                <p className="text-gray-700" data-id="element-2328">
                  Driver must notify employer of any traffic violation (except
                  parking), regardless of nature of violation or type of vehicle
                  driven
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-2329">
                <p className="text-gray-800 mb-2" data-id="element-2330">
                  <strong data-id="element-2331">By end of next business day:</strong>
                </p>
                <p className="text-gray-700" data-id="element-2332">
                  If license is suspended, revoked, canceled, or driver is
                  disqualified, employer must be notified
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6" data-id="element-2333">
                <p className="text-gray-800 mb-2" data-id="element-2334">
                  <strong data-id="element-2335">Employer Requirements:</strong>
                </p>
                <p className="text-gray-700" data-id="element-2336">
                  Employers may not knowingly use a driver who has more than one
                  license or whose license is suspended, revoked, canceled, or
                  disqualified. Violation may result in civil or criminal
                  penalties.
                </p>
              </div>
            </div>
          </div>

          {/* Penalties */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2337">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2338">Penalties</h2>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-2339">
              <div className="bg-red-50 rounded-lg p-6" data-id="element-2340">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2341">
                  Driver Penalties:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-2342">
                  <li data-id="element-2343">
                    • Civil penalty: up to <strong data-id="element-2344">$2,500</strong>
                  </li>
                  <li data-id="element-2345">
                    • Criminal penalties (aggravated cases): up to{' '}
                    <strong data-id="element-2346">$5,000</strong> in fines and/or up to{' '}
                    <strong data-id="element-2347">90 days</strong> in prison
                  </li>
                </ul>
              </div>

              <div className="bg-orange-50 rounded-lg p-6" data-id="element-2348">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2349">
                  Employer Penalties:
                </h3>
                <p className="text-gray-700" data-id="element-2350">
                  Subject to penalty of up to <strong data-id="element-2351">$10,000</strong> if
                  knowingly using a driver to operate a CMV without a valid CDL
                </p>
              </div>
            </div>
          </div>

          {/* Current Status */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2352">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2353">
              Current Status of the CDL Program
            </h2>

            <p className="text-gray-700 mb-4" data-id="element-2354">
              Over 8 million drivers have passed the knowledge and skills tests
              and obtained a CDL. Approximately 11 percent of these CDL drivers
              have been disqualified at least once during the period of April
              1992 through June 1996.
            </p>

            <div className="space-y-4" data-id="element-2355">
              <div data-id="element-2356">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-2357">
                  Current Enhancements:
                </h3>
                <ul className="space-y-1 text-gray-700 ml-4" data-id="element-2358">
                  <li data-id="element-2359">• Driver Data Exchange With Canada and Mexico</li>
                  <li data-id="element-2360">• CDL Judicial Outreach Project (JOP)</li>
                </ul>
              </div>

              <div data-id="element-2361">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-2362">
                  Future Enhancements Being Considered:
                </h3>
                <ul className="space-y-1 text-gray-700 ml-4" data-id="element-2363">
                  <li data-id="element-2364">• Graduated Commercial Licenses</li>
                  <li data-id="element-2365">• Third Party CDL Knowledge Testing</li>
                  <li data-id="element-2366">
                    • Merging Medical Fitness Determination Into CDL Process
                  </li>
                  <li data-id="element-2367">• Simulator Validation for Training & Testing</li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-2368">
            <h2 className="text-3xl font-bold mb-4" data-id="element-2369">
              Need Help with CDL Testing & Requirements?
            </h2>
            <p className="text-xl mb-6" data-id="element-2370">
              Expert guidance on CDL testing, qualifications, and compliance
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-2371">
              <Phone className="mr-3" size={24} data-id="element-2372" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-2373">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}